import type { GLightboxInstance, GLightboxOptions } from './glightbox';

type GlightboxModule = typeof import('./glightbox');

let glightboxModule: Promise<GlightboxModule> | null = null;
let videoLightbox: GLightboxInstance | null = null;

function loadGlightbox(): Promise<GlightboxModule> {
	if (!glightboxModule) {
		glightboxModule = import('./glightbox').catch((error: unknown) => {
			glightboxModule = null;
			throw error;
		});
	}
	return glightboxModule;
}

async function ensureVideoLightbox(): Promise<GLightboxInstance> {
	const mod = await loadGlightbox();
	if (!videoLightbox) {
		videoLightbox = mod.createGLightbox({ selector: '.fly-js-video-modal' });
	}
	return videoLightbox;
}

export function initDeferredSentry(): void {
	const dsn = typeof FF_SENTRY_DSN === 'string' ? FF_SENTRY_DSN : '';
	if (!dsn) {
		return;
	}

	const load = () => {
		void import('../instrument');
	};

	const schedule =
		typeof window.requestIdleCallback === 'function'
			? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 4000 })
			: (cb: () => void) => window.addEventListener('load', cb, { once: true });

	schedule(load);
}

export function initLazyChromeGlightbox(): void {
	const triggers = document.querySelectorAll('.fly-js-video-modal, .js-htmlVideoTrigger');
	if (!triggers.length) {
		return;
	}

	triggers.forEach((el) => {
		el.addEventListener(
			'pointerenter',
			() => {
				void loadGlightbox().catch(() => {});
			},
			{ once: true }
		);
	});

	document.addEventListener(
		'click',
		async (e) => {
			const target = e.target;
			if (!(target instanceof Element)) {
				return;
			}

			const videoTrigger = target.closest('.fly-js-video-modal');
			if (videoTrigger) {
				e.preventDefault();
				e.stopImmediatePropagation();
				try {
					const instance = await ensureVideoLightbox();
					const slides = document.querySelectorAll('.fly-js-video-modal');
					const index = Array.from(slides).indexOf(videoTrigger);
					if (index >= 0) {
						instance.openAt(index);
					}
				} catch {
					if (videoTrigger instanceof HTMLAnchorElement) {
						window.location.assign(videoTrigger.href);
					}
				}
				return;
			}

			const htmlTrigger = target.closest('.js-htmlVideoTrigger');
			if (!(htmlTrigger instanceof HTMLAnchorElement)) {
				return;
			}

			e.preventDefault();
			const href = htmlTrigger.href;
			try {
				const mod = await loadGlightbox();
				const html = `
<div class="htmlVideoPlayerWrapper" style="display: flex; align-items: center;">
	<video autoplay controls style="width: 100%; height: auto;">
		<source src="${href}" type="video/mp4">
	</video>
</div>`;

				mod.openInlineHtml(html, {
					width: 'auto',
					height: 'auto',
					draggable: false,
					touchNavigation: false,
				} satisfies GLightboxOptions);
			} catch {
				window.location.assign(href);
			}
		},
		true
	);
}
